import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { compressImages } from '../../utils/imageUtils';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
  Chip,
  Alert,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Tabs,
  Tab,
  Paper,
  useTheme,
  useMediaQuery,
  Stack,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableRow
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Photo as PhotoIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import axiosInstance from '../../utils/axios';
import { RootState } from '../../store';
import { Task, TaskStatus, UserRole, Company, Individual, User, Category, Branch } from '../../types';
import Autocomplete from '@mui/material/Autocomplete';
import TaskDetail from './TaskDetail';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers';
import { useNavigate } from 'react-router-dom';
import Menu from '@mui/material/Menu';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface TaskFormValues {
  location: string;
  dateTime: string;
  status: TaskStatus;
  notes?: string;
  companyId?: string;
  individualId?: string;
  assignedToId?: string;
  responsibleId?: string;
  categoryId: string;
  branchId?: string;
  amount?: number | string;
  gastoEmpleados?: number | string;
  gastoInsumos?: number | string;
  gastoMaquinas?: number | string;
}

const validationSchema = Yup.object({
  location: Yup.string().required('El nombre de servicio es requerido'),
  dateTime: Yup.string().required('La fecha y hora son requeridas'),
  status: Yup.string().oneOf(Object.values(TaskStatus)).required('El estado es requerido'),
  notes: Yup.string(),
  amount: Yup.number().positive('El monto debe ser positivo').optional().nullable(),
  gastoEmpleados: Yup.number().min(0, 'El gasto no puede ser negativo').optional().nullable(),
  gastoInsumos: Yup.number().min(0, 'El gasto no puede ser negativo').optional().nullable(),
  gastoMaquinas: Yup.number().min(0, 'El gasto no puede ser negativo').optional().nullable(),
  companyId: Yup.string().optional(),
  individualId: Yup.string().optional(),
  branchId: Yup.string().optional(),
  responsibleId: Yup.string(),
  categoryId: Yup.string().required('La categoría es requerida')
}).test('company-or-individual', 'Debe seleccionar una empresa o un particular', function(value) {
  const { companyId, individualId } = value;
  return Boolean((companyId && !individualId) || (!companyId && individualId));
});

function toDatetimeLocal(dateString: string) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  // Convertir a local para el input
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60 * 1000);
  return localDate.toISOString().slice(0, 16);
}

function toLocalISOString(date: Date) {
  // Devuelve YYYY-MM-DDTHH:mm (sin zona horaria)
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const Tasks: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const isManager = user?.role === UserRole.MANAGER;
  const [tasks, setTasks] = useState<Task[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [individuals, setIndividuals] = useState<Individual[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [openPhotoDialog, setOpenPhotoDialog] = useState(false);
  const [beforeFiles, setBeforeFiles] = useState<File[]>([]);
  const [duringFiles, setDuringFiles] = useState<File[]>([]);
  const [afterFiles, setAfterFiles] = useState<File[]>([]);
  const [photoType, setPhotoType] = useState<'before' | 'during' | 'after' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [responsibleId, setResponsibleId] = useState<string | undefined>(undefined);
  const [taskFilter, setTaskFilter] = useState<string>('');
  const [clientFilter, setClientFilter] = useState<string>('');
  const [responsibleFilter, setResponsibleFilter] = useState<string>('');
  const [branchFilter, setBranchFilter] = useState<string>('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [searchTask, setSearchTask] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [dateRange, setDateRange] = useState<{
    start: Date | null;
    end: Date | null;
  }>({
    start: null,
    end: null
  });
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRowId, setMenuRowId] = useState<string | null>(null);
  const [dialogReady, setDialogReady] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchTasks();
    fetchCompanies();
    fetchIndividuals();
    fetchBranches();
    fetchUsers();
    fetchCategories();
    setResponsibleId(user?.id);
  }, []);

  useEffect(() => {
    if (openDialog && users.length > 0) {
      // Determinar responsibleId inicial válido
      const initialResponsibleId = user?.role === UserRole.MANAGER
        ? String(user.id)
        : (selectedTask?.responsibleId && users.some(u => String(u.id) === String(selectedTask.responsibleId))
            ? String(selectedTask.responsibleId)
            : '');
      // Si el responsibleId es válido o vacío, el diálogo está listo
      setDialogReady(true);
    } else {
      setDialogReady(false);
    }
  }, [openDialog, users, selectedTask, user]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/tasks');
      if (response.data && Array.isArray(response.data)) {
        setTasks(response.data);
        setError(null);
      } else {
        console.error('Invalid response format:', response.data);
        setError('Formato de respuesta inválido');
        setTasks([]);
      }
    } catch (error: any) {
      console.error('Error fetching tasks:', error);
      setError(error.response?.data?.message || 'Error al cargar las tareas');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axiosInstance.get('/companies');
      if (response.data && Array.isArray(response.data)) {
        setCompanies(response.data);
      } else {
        console.error('Invalid companies response format:', response.data);
        setCompanies([]);
      }
    } catch (error: any) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    }
  };

  const fetchIndividuals = async () => {
    try {
      const response = await axiosInstance.get('/individuals');
      if (response.data && Array.isArray(response.data)) {
        setIndividuals(response.data);
      } else {
        console.error('Invalid individuals response format:', response.data);
        setIndividuals([]);
      }
    } catch (error: any) {
      console.error('Error fetching individuals:', error);
      setIndividuals([]);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axiosInstance.get('/branches');
      if (response.data && Array.isArray(response.data)) {
        setBranches(response.data);
      } else {
        setBranches([]);
      }
    } catch (error: any) {
      console.error('Error fetching branches:', error);
      setBranches([]);
    }
  };

  const fetchUsers = async () => {
    try {
      console.log('🔍 Fetching users for role:', user?.role);
      let response;
      if (user?.role === UserRole.MANAGER) {
        response = await axiosInstance.get('/users/cleaners');
      } else {
        response = await axiosInstance.get('/users');
      }
      console.log('📡 Users response:', response.data);
      if (response.data && Array.isArray(response.data)) {
        setUsers(response.data);
        console.log('✅ Users set successfully:', response.data.length, 'users');
      } else {
        console.error('❌ Invalid users response format:', response.data);
        setUsers([]);
      }
    } catch (error: any) {
      console.error('❌ Error fetching users:', error);
      setError('Error al cargar los usuarios');
      setUsers([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/categories');
      if (response.data && Array.isArray(response.data)) {
        setCategories(response.data);
      } else {
        setCategories([]);
      }
    } catch (error) {
      setCategories([]);
    }
  };

  const handleOpenDialog = (task?: Task) => {
    setSelectedTask(task || null);
    setOpenDialog(true);
    // Cargar usuarios cuando se abre el diálogo
    fetchUsers();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTask(null);
  };

  const handleSubmit = async (values: TaskFormValues) => {
    console.log('SUBMIT TASK VALUES:', values);
    try {
      if (selectedTask) {
        await axiosInstance.put(`/tasks/${selectedTask.id}`, values);
      } else {
        await axiosInstance.post('/tasks', values);
      }
      fetchTasks();
      handleCloseDialog();
      setError(null);
    } catch (error) {
      console.error('Error saving task:', error);
      setError('Error al guardar la tarea');
    }
  };

  const handleDelete = async (taskId: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar esta tarea?')) {
      try {
        await axiosInstance.delete(`/tasks/${taskId}`);
        fetchTasks();
        setError(null);
      } catch (error) {
        console.error('Error deleting task:', error);
        setError('Error al eliminar la tarea');
      }
    }
  };

  const handleCancel = async (taskId: string) => {
    if (!window.confirm('¿Está seguro que desea cancelar esta tarea? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await axiosInstance.put(`/tasks/${taskId}/cancel`);
      fetchTasks(); // Recargar las tareas
      setError(null);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error al cancelar la tarea');
    }
  };

  const handleComplete = async (taskId: string) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    setSelectedTask(task);
    setPhotoType(null);
    setBeforeFiles([]);
    setDuringFiles([]);
    setAfterFiles([]);
    setOpenPhotoDialog(true);
  };

  const handlePhotoUpload = async () => {
    if (!photoType) {
      setError('Debes seleccionar el tipo de foto.');
      return;
    }
    const formData = new FormData();
    const filesToUpload = photoType === 'before' ? beforeFiles : photoType === 'during' ? duringFiles : afterFiles;
    
    // Comprimir imágenes
    const compressedFiles = await compressImages(filesToUpload);
    compressedFiles.forEach(file => {
      formData.append('photos', file);
    });
    formData.append('type', photoType);

    try {
      await axiosInstance.post(`/tasks/${selectedTask?.id}/complete`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      fetchTasks();
      setOpenPhotoDialog(false);
      setBeforeFiles([]);
      setDuringFiles([]);
      setAfterFiles([]);
      setPhotoType(null);
      setError(null);
    } catch (error) {
      console.error('Error uploading photo:', error);
      setError('Error al subir las fotos');
    }
  };

  const handleDateRangeChange = (type: 'start' | 'end', value: Date | null) => {
    setDateRange(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const handleQuickDateSelect = (range: 'today' | 'tomorrow' | 'thisWeek') => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let startDate: Date;
    let endDate: Date;

    switch (range) {
      case 'today':
        startDate = today;
        endDate = new Date(today.getTime() + 24 * 60 * 60 * 1000);
        break;
      case 'tomorrow':
        startDate = new Date(today);
        startDate.setDate(today.getDate() + 1);
        endDate = new Date(startDate.getTime() + 24 * 60 * 60 * 1000);
        break;
      case 'thisWeek':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - today.getDay());
        endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        break;
      default:
        return;
    }

    setDateRange({
      start: startDate,
      end: endDate
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, rowId: string) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const filteredTasks = Array.isArray(tasks) ? tasks.filter(task => {
    if (taskFilter && task.status !== taskFilter) return false;
    if (clientFilter) {
      const isCompany = task.companyId === clientFilter;
      const isIndividual = task.individualId === clientFilter;
      if (!isCompany && !isIndividual) return false;
    }
    if (responsibleFilter && task.responsibleId !== responsibleFilter) return false;
    if (branchFilter && task.branchId !== branchFilter) return false;
    if (searchTask && !(
      task.location.toLowerCase().includes(searchTask.toLowerCase()) ||
      (task.company?.name || '').toLowerCase().includes(searchTask.toLowerCase()) ||
      (task.responsible?.firstName || '').toLowerCase().includes(searchTask.toLowerCase())
    )) return false;

    // Filtro por fecha
    const taskDate = new Date(task.dateTime);
    if (dateRange.start && taskDate < dateRange.start) return false;
    if (dateRange.end) {
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59, 999);
      if (taskDate > endDate) return false;
    }

    return true;
  }) : [];

  const columns: GridColDef[] = [
    { 
      field: 'location', 
      headerName: 'Servicio', 
      flex: 1,
      minWidth: isMobile ? 120 : 150,
      sortable: true,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'dateTime', 
      headerName: 'Fecha', 
      flex: 1,
      minWidth: isMobile ? 100 : 120,
      sortable: true,

      valueGetter: (params) => {
        // Para ordenamiento, devolver la fecha original como string ISO
        return params.row.dateTime;
      },
      renderCell: (params: GridRenderCellParams) => {
        // Formatear la fecha para mostrar
        const date = new Date(params.row.dateTime);
        const formattedDate = isMobile ? date.toLocaleDateString() : date.toLocaleString();
        
        return (
          <Typography variant="body2" noWrap>
            {formattedDate}
          </Typography>
        );
      }
    },
    { 
      field: 'client', 
      headerName: 'Cliente', 
      flex: 1,
      minWidth: isMobile ? 100 : 120,
      valueGetter: (params) => {
        if (params.row.company?.name) {
          return `🏢 ${params.row.company.name}`;
        } else if (params.row.individual?.name) {
          return `👤 ${params.row.individual.name}`;
        }
        return '';
      },
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'status', 
      headerName: 'Estado', 
      flex: 1,
      minWidth: isMobile ? 80 : 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value === 'COMPLETED' ? 'Completada' : 
                 params.value === 'IN_PROGRESS' ? 'En Progreso' :
                 params.value === 'CANCELLED' ? 'Cancelada' : 'Pendiente'}
          color={
            params.value === 'COMPLETED' ? 'success' :
            params.value === 'IN_PROGRESS' ? 'warning' :
            params.value === 'CANCELLED' ? 'error' : 'default'
          }
          size="small"
          sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      flex: 1,
      minWidth: isMobile ? 80 : 150,
      renderCell: (params: GridRenderCellParams) => (
        isMobile ? (
          <>
            <IconButton onClick={e => handleMenuOpen(e, params.row.id)}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl) && menuRowId === params.row.id}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => { navigate(`/tasks/${params.row.id}`); handleMenuClose(); }}>Ver</MenuItem>
              <MenuItem onClick={() => { handleOpenDialog(params.row); handleMenuClose(); }}>Editar</MenuItem>
              {params.row.status !== 'COMPLETED' && params.row.status !== 'CANCELLED' && (
                <MenuItem onClick={() => { handleComplete(params.row.id); handleMenuClose(); }}>Completar</MenuItem>
              )}
              {user?.role === UserRole.DIRECTOR && params.row.status !== 'CANCELLED' && (
                <MenuItem onClick={() => { handleCancel(params.row.id); handleMenuClose(); }}>Anular</MenuItem>
              )}
            </Menu>
          </>
        ) : (
          <Stack direction="row" spacing={0.5}>
            <Button 
              size="small" 
              variant="outlined"
              onClick={() => navigate(`/tasks/${params.row.id}`)}
              sx={{ fontSize: '0.875rem', minWidth: 0 }}
            >
              Ver
            </Button>
            <Button 
              size="small" 
              variant="outlined"
              onClick={() => handleOpenDialog(params.row)}
              sx={{ fontSize: '0.875rem', minWidth: 0 }}
            >
              Editar
            </Button>
            {params.row.status !== 'COMPLETED' && params.row.status !== 'CANCELLED' && (
              <>
                <Button 
                  size="small" 
                  color="success" 
                  variant="contained"
                  onClick={() => handleComplete(params.row.id)}
                  sx={{ fontSize: '0.875rem', minWidth: 0 }}
                >
                  Completar
                </Button>
                {user?.role === UserRole.DIRECTOR && params.row.status !== 'CANCELLED' && (
                  <Button 
                    size="small" 
                    color="error" 
                    variant="outlined"
                    onClick={() => handleCancel(params.row.id)}
                    sx={{ fontSize: '0.875rem', minWidth: 0 }}
                  >
                    Anular
                  </Button>
                )}
              </>
            )}
          </Stack>
        )
      )
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => {
            setError(null);
            fetchTasks();
          }}
        >
          Reintentar
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', boxSizing: 'border-box', overflowX: 'visible' }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexDirection={isMobile ? 'column' : 'row'}
        gap={isMobile ? 2 : 0}
      >
        <Typography
          variant="h4"
          sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, textAlign: { xs: 'center', sm: 'left' }, width: { xs: '100%', sm: 'auto' }, mb: { xs: 1, sm: 0 } }}
        >
          Tareas
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          fullWidth={isMobile}
          sx={{ mt: { xs: 1, sm: 0 }, borderRadius: 2, mb: { xs: 1, sm: 0 } }}
        >
          Nueva Tarea
        </Button>
      </Box>

      <Paper sx={{ width: '100%', mb: { xs: 3, sm: 3 }, boxSizing: 'border-box', overflowX: 'visible', borderRadius: { xs: 3, sm: 2 }, mt: { xs: 1, sm: 0 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 1, sm: 0 }}>
          <Typography variant="h6" sx={{ width: { xs: '100%', sm: 'auto' }, textAlign: { xs: 'center', sm: 'left' } }}>Filtros</Typography>
          {isMobile && (
            <IconButton onClick={() => setShowFilters(!showFilters)}>
              <FilterIcon />
            </IconButton>
          )}
        </Box>
        <Box sx={{ display: { xs: showFilters ? 'block' : 'none', md: 'block' } }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={taskFilter}
                  onChange={e => setTaskFilter(e.target.value)}
                  label="Estado"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="PENDING">Pendiente</MenuItem>
                  <MenuItem value="IN_PROGRESS">En Progreso</MenuItem>
                  <MenuItem value="COMPLETED">Completada</MenuItem>
                  <MenuItem value="CANCELLED">Cancelada</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                <InputLabel>Cliente</InputLabel>
                <Select
                  value={clientFilter}
                  onChange={e => setClientFilter(e.target.value)}
                  label="Cliente"
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="" disabled>
                    <Typography variant="caption" color="text.secondary">🏢 Empresas</Typography>
                  </MenuItem>
                  {companies.map(c => <MenuItem key={c.id} value={c.id}>🏢 {c.name}</MenuItem>)}
                  <MenuItem value="" disabled>
                    <Typography variant="caption" color="text.secondary">👤 Particulares</Typography>
                  </MenuItem>
                  {individuals.map(i => <MenuItem key={i.id} value={i.id}>👤 {i.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                <InputLabel>Responsable</InputLabel>
                <Select
                  value={responsibleFilter}
                  onChange={e => setResponsibleFilter(e.target.value)}
                  label="Responsable"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {users.map(u => <MenuItem key={u.id} value={u.id}>{u.firstName} {u.lastName}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                <InputLabel>Sucursal</InputLabel>
                <Select
                  value={branchFilter}
                  onChange={e => setBranchFilter(e.target.value)}
                  label="Sucursal"
                >
                  <MenuItem value="">Todas</MenuItem>
                  {branches.map(b => (
                    <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label="Buscar"
                value={searchTask}
                onChange={e => setSearchTask(e.target.value)}
                fullWidth
                size={isMobile ? 'small' : 'medium'}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
          </Grid>
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>Filtros rápidos de fecha:</Typography>
            <Stack direction={isMobile ? "column" : "row"} spacing={1} sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleQuickDateSelect('today')}
                fullWidth={isMobile}
                sx={{ borderRadius: 2 }}
              >
                Hoy
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleQuickDateSelect('tomorrow')}
                fullWidth={isMobile}
                sx={{ borderRadius: 2 }}
              >
                Mañana
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleQuickDateSelect('thisWeek')}
                fullWidth={isMobile}
                sx={{ borderRadius: 2 }}
              >
                Esta semana
              </Button>
            </Stack>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Desde"
                value={dateRange.start}
                onChange={(date) => handleDateRangeChange('start', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: isMobile ? "small" : "medium"
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Hasta"
                value={dateRange.end}
                onChange={(date) => handleDateRangeChange('end', date)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: isMobile ? "small" : "medium"
                  }
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <div
        style={{
          overflowX: 'auto',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          marginTop: isMobile ? 16 : 24
        }}
      >
        <Box sx={{ minWidth: { xs: 350, sm: 700 }, width: '100%' }}>
          <DataGrid
            rows={filteredTasks}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50]}
            getRowId={row => row.id}
            sortingMode="client"
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            onRowDoubleClick={(params) => {
              navigate(`/tasks/${params.row.id}`);
            }}
            sx={{
              minWidth: { xs: 350, sm: 700 },
              width: '100%',
              '& .MuiDataGrid-cell': {
                fontSize: isMobile ? '0.75rem' : '0.875rem',
              },
              '& .MuiDataGrid-columnHeader': {
                fontSize: isMobile ? '0.75rem' : '0.875rem',
              }
            }}
          />
        </Box>
      </div>
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: { xs: '95vw', sm: 600 },
            m: { xs: 1, sm: 3 },
          }
        }}
      >
        <DialogTitle>
          {selectedTask ? 'Editar Tarea' : 'Nueva Tarea'}
        </DialogTitle>
        {(user?.role === UserRole.MANAGER || users.length > 0) && (
          <Formik
            initialValues={{
              location: selectedTask?.location || '',
              dateTime: selectedTask?.dateTime ? toDatetimeLocal(selectedTask.dateTime) : '',
              status: selectedTask?.status || TaskStatus.PENDING,
              notes: selectedTask?.notes || '',
              companyId: selectedTask?.companyId || '',
              individualId: selectedTask?.individualId || '',
              branchId: selectedTask?.branchId || '',
              amount: selectedTask?.amount || '',
              gastoEmpleados: selectedTask?.gastoEmpleados ?? '',
              gastoInsumos: selectedTask?.gastoInsumos ?? '',
              gastoMaquinas: selectedTask?.gastoMaquinas ?? '',
              responsibleId: user?.role === UserRole.MANAGER ? String(user.id) : (selectedTask?.responsibleId && users.some(u => String(u.id) === String(selectedTask.responsibleId)) ? String(selectedTask.responsibleId) : ''),
              categoryId: selectedTask?.categoryId || '',
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ isSubmitting, setFieldValue, values, errors, touched }) => {
              console.log('RESPONSIBLE DEBUG:', {
                responsibleId: values.responsibleId,
                usersCount: users.length,
                userRole: user?.role,
                users: users
              });
              return (
                <Form>
                  <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          fullWidth
                          margin="normal"
                          name="location"
                          label="Nombre de Servicio"
                          autoComplete="off"
                          error={touched.location && Boolean(errors.location)}
                          helperText={touched.location && errors.location}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          fullWidth
                          margin="normal"
                          name="amount"
                          label="Monto Cobrado (Opcional)"
                          type="number"
                          InputProps={{ inputProps: { min: 0 } }}
                          error={touched.amount && Boolean(errors.amount)}
                          helperText={touched.amount && errors.amount}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                          Gastos (Opcional)
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Field
                              as={TextField}
                              fullWidth
                              name="gastoEmpleados"
                              label="Empleados"
                              type="number"
                              InputProps={{ inputProps: { min: 0 } }}
                              error={touched.gastoEmpleados && Boolean(errors.gastoEmpleados)}
                              helperText={touched.gastoEmpleados && errors.gastoEmpleados}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Field
                              as={TextField}
                              fullWidth
                              name="gastoInsumos"
                              label="Insumos"
                              type="number"
                              InputProps={{ inputProps: { min: 0 } }}
                              error={touched.gastoInsumos && Boolean(errors.gastoInsumos)}
                              helperText={touched.gastoInsumos && errors.gastoInsumos}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Field
                              as={TextField}
                              fullWidth
                              name="gastoMaquinas"
                              label="Alquiler de Máquinas"
                              type="number"
                              InputProps={{ inputProps: { min: 0 } }}
                              error={touched.gastoMaquinas && Boolean(errors.gastoMaquinas)}
                              helperText={touched.gastoMaquinas && errors.gastoMaquinas}
                              size="small"
                            />
                          </Grid>
                        </Grid>
                        {(values.amount || values.gastoEmpleados || values.gastoInsumos || values.gastoMaquinas) && (() => {
                          const monto = parseFloat(String(values.amount || 0)) || 0;
                          const gastos = (parseFloat(String(values.gastoEmpleados || 0)) || 0)
                            + (parseFloat(String(values.gastoInsumos || 0)) || 0)
                            + (parseFloat(String(values.gastoMaquinas || 0)) || 0);
                          const neto = monto - gastos;
                          return (
                            <Typography variant="body2" sx={{ mt: 1, color: neto >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold' }}>
                              Neto: ${neto.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                            </Typography>
                          );
                        })()}
                      </Grid>
                      <Grid item xs={12}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DateTimePicker
                            label="Fecha y Hora"
                            value={values.dateTime ? new Date(values.dateTime) : null}
                            onChange={value => {
                              let localDateTime = '';
                              if (value && typeof (value as unknown as Date).toISOString === 'function') {
                                // Enviar la fecha en formato local sin zona horaria
                                const date = value as unknown as Date;
                                const year = date.getFullYear();
                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                const day = String(date.getDate()).padStart(2, '0');
                                const hours = String(date.getHours()).padStart(2, '0');
                                const minutes = String(date.getMinutes()).padStart(2, '0');
                                localDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
                              }
                              setFieldValue('dateTime', localDateTime);
                            }}
                            minutesStep={10}
                            ampm={false}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                margin: "normal",
                                name: "dateTime",
                                error: touched.dateTime && Boolean(errors.dateTime),
                                helperText: touched.dateTime && errors.dateTime
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>
                          Cliente
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Autocomplete
                              options={companies}
                              getOptionLabel={(option) => option.name}
                              value={companies.find(c => c.id === values.companyId) || null}
                              onChange={(_, newValue) => {
                                setFieldValue('companyId', newValue ? newValue.id : '');
                                setFieldValue('individualId', ''); // Limpiar particular si se selecciona empresa
                                if (newValue && newValue.branchId) {
                                  setFieldValue('branchId', newValue.branchId);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Empresa"
                                  margin="normal"
                                  fullWidth
                                  error={touched.companyId && Boolean(errors.companyId)}
                                  helperText={touched.companyId && errors.companyId}
                                />
                              )}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Autocomplete
                              options={individuals}
                              getOptionLabel={(option) => option.name}
                              value={individuals.find(i => i.id === values.individualId) || null}
                              onChange={(_, newValue) => {
                                setFieldValue('individualId', newValue ? newValue.id : '');
                                setFieldValue('companyId', ''); // Limpiar empresa si se selecciona particular
                                if (newValue && newValue.branchId) {
                                  setFieldValue('branchId', newValue.branchId);
                                }
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Particular"
                                  margin="normal"
                                  fullWidth
                                  error={touched.individualId && Boolean(errors.individualId)}
                                  helperText={touched.individualId && errors.individualId}
                                />
                              )}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <FormControl fullWidth margin="normal">
                              <InputLabel>Sucursal</InputLabel>
                              <Select
                                value={values.branchId || ''}
                                label="Sucursal"
                                onChange={(e) => setFieldValue('branchId', e.target.value)}
                              >
                                <MenuItem value="">
                                  <em>Ninguna</em>
                                </MenuItem>
                                {branches.map((branch) => (
                                  <MenuItem key={branch.id} value={branch.id}>
                                    {branch.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xs={12}>
                        {users.length > 0 && (
                          <FormControl fullWidth error={touched.responsibleId && Boolean(errors.responsibleId)}>
                            <InputLabel id="responsible-select-label">Responsable</InputLabel>
                            <Select
                              labelId="responsible-select-label"
                              id="responsibleId"
                              name="responsibleId"
                              value={user?.role === UserRole.MANAGER ? String(user.id) : (values.responsibleId || '')}
                              label="Responsable"
                              onChange={e => setFieldValue("responsibleId", e.target.value)}
                              disabled={user?.role === UserRole.MANAGER}
                              MenuProps={{
                                PaperProps: { style: { maxHeight: 300 } }
                              }}
                            >
                              <MenuItem value="">
                                <em>Ninguno</em>
                              </MenuItem>
                              {users.map(userItem => (
                                <MenuItem key={userItem.id} value={String(userItem.id)}>
                                  {userItem.firstName} {userItem.lastName} ({userItem.role})
                                </MenuItem>
                              ))}
                            </Select>
                            {touched.responsibleId && errors.responsibleId && (
                              <FormHelperText>{errors.responsibleId}</FormHelperText>
                            )}
                          </FormControl>
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Autocomplete
                          options={categories}
                          getOptionLabel={(option) => option.name}
                          value={categories.find(c => c.id === values.categoryId) || null}
                          onChange={(_, newValue) => setFieldValue('categoryId', newValue ? newValue.id : '')}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Categoría"
                              margin="normal"
                              fullWidth
                              error={touched.categoryId && Boolean(errors.categoryId)}
                              helperText={touched.categoryId && typeof errors.categoryId === 'string' ? errors.categoryId : ''}
                            />
                          )}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Field
                          as={TextField}
                          fullWidth
                          margin="normal"
                          name="notes"
                          label="Notas"
                          multiline
                          rows={4}
                          error={touched.notes && Boolean(errors.notes)}
                          helperText={touched.notes && errors.notes}
                        />
                      </Grid>
                    </Grid>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button type="submit" variant="contained" disabled={isSubmitting}>
                      {isSubmitting ? <CircularProgress size={24} /> : (selectedTask ? 'Guardar Cambios' : 'Crear Tarea')}
                    </Button>
                  </DialogActions>
                </Form>
              );
            }}
          </Formik>
        )}
      </Dialog>
      {showDetail && selectedTaskId && (
        <TaskDetail
          open={showDetail}
          onClose={() => setShowDetail(false)}
          taskId={selectedTaskId}
          onTaskUpdated={fetchTasks}
        />
      )}
      <Dialog open={openPhotoDialog} onClose={() => setOpenPhotoDialog(false)}>
        <DialogTitle>Completar Tarea</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Estado de las fotos:
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Fotos Antes: {selectedTask?.beforePhoto?.length ? `✅ ${selectedTask.beforePhoto.length} subidas` : '❌ Ninguna'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Fotos Durante: {selectedTask?.duringPhoto?.length ? `✅ ${selectedTask.duringPhoto.length} subidas` : '❌ Ninguna'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Fotos Después: {selectedTask?.afterPhoto?.length ? `✅ ${selectedTask.afterPhoto.length} subidas` : '❌ Ninguna'}
            </Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Selecciona el tipo de foto:
            </Typography>
            <Button
              variant={photoType === 'before' ? 'contained' : 'outlined'}
              onClick={() => setPhotoType('before')}
              sx={{ mr: 1 }}
            >
              Fotos Antes
            </Button>
            <Button
              variant={photoType === 'during' ? 'contained' : 'outlined'}
              onClick={() => setPhotoType('during')}
              sx={{ mr: 1 }}
            >
              Fotos Durante
            </Button>
            <Button
              variant={photoType === 'after' ? 'contained' : 'outlined'}
              onClick={() => setPhotoType('after')}
            >
              Fotos Después
            </Button>
          </Box>
          {photoType && (
            <Box sx={{ mt: 2 }}>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  if (photoType === 'before') setBeforeFiles(files);
                  if (photoType === 'during') setDuringFiles(files);
                  if (photoType === 'after') setAfterFiles(files);
                }}
              />
              <Typography variant="caption" color="textSecondary">
                Puedes seleccionar varias imágenes. Al menos una es obligatoria.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPhotoDialog(false)}>Cancelar</Button>
          <Button
            onClick={handlePhotoUpload}
            variant="contained"
            color="primary"
            disabled={photoType === 'before' ? beforeFiles.length === 0 : photoType === 'during' ? duringFiles.length === 0 : afterFiles.length === 0}
          >
            Subir Foto(s)
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tasks; 